import type { Theme } from "@react-navigation/native"

const palette = {
  neutral100: "#FFFFFF",
  neutral200: "#F4F2F1",
  neutral300: "#D7CEC9",
  neutral400: "#B6ACA6",
  neutral500: "#978F8A",
  neutral600: "#564E4A",
  neutral700: "#3C3836",
  neutral750: "#303030",
  neutral775: "#404040",
  neutral800: "#191015",
  neutral850: "#808080",
  neutral900: "#000000",

  primary100: "#D0E9F9", // (lightest)
  primary200: "#A1D3F3",
  primary300: "#72BDEB",
  primary400: "#439CE0", // (base color)
  primary500: "#3177B3",
  primary600: "#245285", // (darkest)

  secondary100: "#DCDDE9",
  secondary200: "#BCC0D6",
  secondary300: "#9196B9",
  secondary400: "#626894",
  secondary500: "#41476E",

  accent100: "#FFEED4",
  accent200: "#FFE1B2",
  accent300: "#FDD495",
  accent400: "#FBC878",
  accent500: "#FFBB50",

  angry100: "#F2D6CD",
  angry500: "#C03403",

  overlay20: "rgba(25, 16, 21, 0.2)",
  overlay50: "rgba(25, 16, 21, 0.5)",
  overlay80: "rgba(25, 16, 21, 0.8)",

  shadow: "#3C3836",

  // Primary Colors:
  skText: "#FFFFFF",
  skTextDim: "rgba(255, 255, 255, 0.5)",
  skBackground: "#000000",
  skFooterBackground: "#303030",

  // List Items
  skListItemCategoryBackground: "#333333",
  skListItemBackground: "rgba(255, 255, 255, 0.1)",
  skListItemAccent: "#439CE0",

  skAccessoryBackground: "#141414",
} as const

export const colorsDark = {
  /**
   * The palette is available to use, but prefer using the name.
   * This is only included for rare, one-off cases. Try to use
   * semantic names as much as possible.
   */
  palette,
  primary: palette.primary400,
  /**
   * A helper for making something see-thru.
   */
  transparent: "rgba(0, 0, 0, 0)",
  /**
   * The default text color in many components.
   */
  text: palette.skText,
  /**
   * Secondary text information.
   */
  textDim: palette.neutral850,
  /**
   * The default color of the screen background.
   */
  background: "#141414",
  /**
   * The default border color.
   */
  border: palette.neutral400,
  /**
   * The main tinting color.
   */
  tint: palette.primary500,
  /**
   * A subtle color used for lines.
   */
  separator: palette.neutral300,
  /**
   * The inactive tinting color.
   */
  tintInactive: palette.skFooterBackground,
  /**
   * Error messages.
   */
  error: palette.angry500,
  /**
   * Error Background.
   */
  errorBackground: palette.angry100,
  /**
   * The Color of Shadows.
   * This is a special color that is used to create shadows in the app.
   */
  shadow: palette.shadow,

  white: "#FFFFFF",
  buttonBackground: "#303030",
  buttonText: "#FFFFFF",
  menuButtonText: "#FFFFFF",
  buttonDisabledBackground: "rgba(48, 48, 48, 0.4)",

  // Button variant backgrounds (normal state)
  buttonDefaultBackground: palette.neutral100, // white fill on dark bg
  buttonDefaultBorder: palette.neutral400, // mid-gray border
  buttonFilledBackground: palette.neutral300, // light gray fill
  buttonReversedBackground: palette.neutral800, // near-black (blends into dark bg)
  buttonReversedText: palette.neutral100, // white text on dark reversed bg

  // Button variant backgrounds (pressed state)
  buttonDefaultPressedBackground: palette.neutral200,
  buttonFilledPressedBackground: palette.neutral400,
  buttonReversedPressedBackground: palette.neutral700,
  inputBackground: "#1A1A1A",
  inputText: "#FFFFFF",
  inputBorder: "#404040",
  userBubbleBackground: "#303030",
  contentCardTitleBackground: "#303030",
  contentCardTopicBackground: "#404040",
  userBubbleText: "#FFFFFF",
  sidekickBubbleBackground: "#141414",
  sidekickBubbleText: "#FFFFFF",
  avatarBackground: "#303030",
  avatarBorder: "#FFFFFF",
  activeAvatarBackground: "#FFFFFF",
  activeAvatarBorder: "#FFFFFF",
  avatarCaptionBackground: "#404040",
  avatarCaptionText: "#FFFFFF",
  captionTextShadow: "rgba(0, 0, 0, 0.9)",
  avatarControlButtonBackground: "#404040",
  avatarControlButtonIcon: "#FFFFFF",
  pillUnselected: "#404040",
  pillSelected: "#686868",
  sectionSeparator: "#808080",
  cardBorder: "#303030",
  mskProfileBackground: "#404040",
  mskProfileBorder: "#FFFFFF",
  thinkingDot: "#FFFFFF",
} as const

export const ExpoRouterDarkTheme: Theme = {
  dark: true,
  colors: {
    primary: colorsDark.palette.primary400,
    background: colorsDark.background,
    card: "#1A1A1A",
    text: colorsDark.text,
    border: colorsDark.border,
    notification: colorsDark.error,
  },
  fonts: {
    // Using the same font configuration as light theme
    regular: {
      fontFamily: "figtreeRegular",
      fontWeight: "normal",
    },
    medium: {
      fontFamily: "figtreeMedium",
      fontWeight: "normal",
    },
    bold: {
      fontFamily: "figtreeSemiBold",
      fontWeight: "normal",
    },
    heavy: {
      fontFamily: "figtreeBold",
      fontWeight: "normal",
    },
  },
}
